import CheckIcon from '@mui/icons-material/Check';
import { Box, ButtonBase, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';

interface IScheduleStepProgressProps {
	step: number;
	stepTitle: string;
	selected?: boolean;
	completed?: boolean;
	disabled?: boolean;
}

export default function ScheduleStepProgress({
	step,
	stepTitle,
	selected,
	completed,
	disabled = false,
}: IScheduleStepProgressProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { setShouldPreserveAppointmentAndCenterData } = useAppointmentInformationStore();
	const { setSpecifiedStep } = useStepManagerStore((store) => store);
	const { t } = useTranslation('index');

	return (
		<Box id={`step-progress-${step}`} sx={{ scrollMarginTop: '3.4rem' }}>
			<ButtonBase
				sx={{
					position: 'relative',
					width: '100%',
					py: { md: 2, xs: 1 },
					px: { md: 2, xs: 0 },
					paddingTop: { md: 2, xs: 4 },
					textAlign: 'left',
					justifyContent: 'flex-start',
					cursor: completed ? 'pointer' : 'default',
					...(selected ? { bgcolor: { md: 'backgrounds.light', xs: 'none' } } : {}),
				}}
				onClick={() => {
					if (completed) {
						if (step === 3) {
							setShouldPreserveAppointmentAndCenterData(true);
						}
						setSpecifiedStep(step);
					}
				}}
				data-test-id={`section_schedule_step_progress`}
				disabled={disabled}
			>
				{selected && !isSmallScreen && (
					<Box position="absolute" left={0} bgcolor="text.primary" width={4} height="100%" />
				)}
				<Stack
					alignItems="center"
					justifyContent="center"
					width={24}
					height={24}
					minWidth={24}
					bgcolor={completed ? { md: 'success.dark' } : selected ? 'text.primary' : 'neutrals.disabled'}
					borderRadius="50%"
					mr={{ md: 2, xs: 1.25 }}
				>
					{completed ? (
						<CheckIcon sx={{ color: { md: 'text.light', xs: 'secondary.main' }, width: 22 }} />
					) : (
						<Typography variant="lowestLevelBook" color={'text.light'}>
							{step}
						</Typography>
					)}
				</Stack>
				<Typography
					color={selected || completed ? 'text.primary' : 'text.tertiary'}
					variant={isSmallScreen ? 'bodySmallBook' : 'bodyLargeSemiBold'}
					fontWeight={isSmallScreen ? 350 : 500}
					{...(step === 4 && isSmallScreen && selected
						? {
								'data-private': true,
						  }
						: {})}
				>
					{t(stepTitle)}
				</Typography>
			</ButtonBase>
		</Box>
	);
}
